import React, { PureComponent } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import MediaQuery from 'react-responsive';
import Waypoint from 'react-waypoint';
import classNames from 'classnames';
import I18n from '@fiverr-private/fiverr_context/src/I18n';

import createGigStore from '../../store';
import { isTriplePackage, isSinglePackage } from '../../reducers/packages';

import scrollToComponent from '../../utils/scrollToComponent';
import { isTouchDevice, isTouchDeviceByContext } from '../../utils/isTouchDevice';
import reformatCollections from '../../utils/reformatCollections';
import ImpressionsFactory from '../../utils/impressions/factory';

import {
    OVERVIEW,
    DESCRIPTION,
    FAQ,
    REVIEWS,
    ORDER_DETAILS,
    RECOMMENDATIONS,
    ABOUT_SELLER,
    PACKAGES_TABLE
} from '../../utils/pageSections';
import {
    validateDescription,
    validateOverview,
    validateFaq,
    validateReviews,
    validateOrderDetails,
    validateSellerCard,
    validatePackagesTable
} from '../../utils/mainComponentsValidator';
import BiEvents from '../../utils/biEvents';
import GigCurrencyConverter from '../../utils/gigCurrencyConverter';
import { MP_SHOW_PAGE_NAME } from '../../utils/mixPanelEvents';
import { SUB_CATEGORIES } from '../../utils/categoriesEnum';
import { isGigPaused, isGigApproved } from '../../utils/gigStatus';
import { isLoggedInUser } from '../../utils/isLoggedInUser';
import Logger from '../../utils/logger';
import PathfinderWrapper from '../../utils/pathfinderWrapper';
import isInFiverrProUpdateTest from '../../utils/experiments/fiverrProUpdates';

import {
    MEDIA_GRID_MD,
    SCROLL_DURATION,
    SCROLL_OFFSET,
    WAYPOINT_OFFSET
} from '../../utils/constants';

import isInPersonalizedContentExp from '../../utils/experiments/personalizedSection';

import SellerCard from '../SellerCard';
import StudioCard from '../StudioCard';
import Description from '../Description';
import Faq from '../Faq';
import Reviews from '../Reviews';
import Tags from '../Tags';
import PackagesTabs from '../PackagesTabs';
import PackagesTable from '../PackagesTable';
import OrderDetails from '../OrderDetails';
import StudioBox from '../StudioBox';
import TopNav from '../TopNav';
import ShareYourGig from '../ShareYourGig';
import Gallery from '../Gallery';
import Overview from '../Overview';
import Portfolio from '../Portfolio';
import OutOfOffice from '../OutOfOffice';
import SimilarGigs from '../SimilarGigs';
import RecommendedInSc from '../RecommendedInSc';
import PersonalizedSectionWrapper from '../PersonalizedSectionWrapper';
import OtherGigsBy from '../OtherGigsBy';
import RecentlyViewedGigs from '../RecentlyViewedGigs';
import Disclaimer from '../Disclaimer';
import MoreServices from '../MoreServices';
import PausedGig from '../PausedGig';
import ContactSeller from '../ContactSeller';
import ProVideo from '../ProVideo';
import ProBanner from '../ProBanner';
import ProCertificateBanner from '../ProCertificateBanner';
import ProBox from '../ProBox';
import ReviewsSnippet from '../ReviewsSnippet';

import './style.scss';

const xss = require('xss');

const sanitizeSeo = (seoJson) => xss(JSON.stringify(seoJson), {
    whiteList: [],
    stripIgnoreTag: true,
    stripIgnoreTagBody: ['a', 'script']
});

class GigPage extends PureComponent {

    constructor(props) {
        super(props);

        const {
            packages,
            studio,
            requestContext,
            topNav,
            general
        } = this.props;

        if (!general || !requestContext) {
            // If general params  or requestContext are missing throw an error and don't render the page
            throw Error('Missing general data or requestContext');
        }

        this.state = {
            collections: topNav ? reformatCollections(topNav.collections) : {},
            sectionKey: OVERVIEW,
            prevSectionKey: OVERVIEW,
            useWaypoints: true,
            hideRecommended: false,
            isTouch: isTouchDeviceByContext(requestContext) || false
        };

        this.scrollToSection = this.scrollToSection.bind(this);
        this.updateCollections = this.updateCollections.bind(this);
        this.onLeaveWaypoint = this.onLeaveWaypoint.bind(this);
        this.onEnterWaypoint = this.onEnterWaypoint.bind(this);
        this.onEmptyRecommendedForYouGigs = this.onEmptyRecommendedForYouGigs.bind(this);
        this.onComparePackagesClick = this.onComparePackagesClick.bind(this);

        this.store = createGigStore({packages, studio, requestContext, general});
        this.useStickyLayout = this.shouldUseStickyLayout();

        // used for unwrapping/rewrapping
        this.orphans = [];
        this.displayContents = true;

        const packagesCount = _.get(this.store.getState(), 'packages.packageList.length', 3);

        this.biEvents = new BiEvents({requestContext, general, packagesCount});
        this.impressionsFactory = new ImpressionsFactory({requestContext, general, packagesCount});
        this.logger = new Logger(requestContext);
        this.pathfinderWrapper = new PathfinderWrapper(this.logger);
    }

    getChildContext() {
        const { requestContext, general, currency, rollouts } = this.props;

        return {
            biEvents: this.biEvents,
            impressionsFactory: this.impressionsFactory,
            requestContext,
            general,
            logger: this.logger,
            currencyConverter: new GigCurrencyConverter(currency),
            pathfinderWrapper: this.pathfinderWrapper,
            rollouts
        };
    }

    componentDidMount() {
        this.setDisplayContentsSupport();
        this.reportPageView();

        // eslint-disable-next-line
        this.setState({
            isTouch: isTouchDevice()
        });

    }

    setDisplayContentsSupport() {
        if (window.CSS && window.CSS.supports) {
            this.displayContents = CSS.supports('display: contents');
        } else if (window.supportsCSS) { // Opera's old version of css detection
            this.displayContents = window.supportsCSS('display:contents');
        } else {
            this.displayContents = false;
        }
    }

    reportPageView() {
        this.biEvents.sendMixPanelEvent({mpEventType: 'trackPageView', eventName: MP_SHOW_PAGE_NAME});
    }

    scrollToSection({sectionKey}) {

        const refsObj = {};

        refsObj[OVERVIEW] = this.sectionOverview;
        refsObj[DESCRIPTION] = this.sectionDescription;
        refsObj[RECOMMENDATIONS] = this.sectionRecommendations;
        refsObj[FAQ] = this.sectionFaq;
        refsObj[REVIEWS] = this.sectionReviews;
        refsObj[ORDER_DETAILS] = this.sectionOrderDetails;
        refsObj[ABOUT_SELLER] = this.sectionAboutSeller;
        refsObj[PACKAGES_TABLE] = this.sectionPackagesTable;

        const currentSection = this.state.sectionKey;

        if ((refsObj[sectionKey]) && (sectionKey !== currentSection)) {

            this.setState({
                sectionKey,
                useWaypoints: false,
                prevSectionKey: currentSection
            }, () => {

                const scroller = scrollToComponent(refsObj[sectionKey], { offset: SCROLL_OFFSET, align: 'top', duration: SCROLL_DURATION });

                // we need a timeout here, otherwise the waypoints fire
                // right at the end of the scroll
                scroller.onComplete(() => {
                    setTimeout(() => {
                        this.setState({
                            useWaypoints: true
                        });
                    }, 50);
                });

            });

        }
    }

    updateCollections(collections) {
        this.setState({collections});
    }

    onEnterWaypoint(newSection, data) {

        const currentSection = this.state.sectionKey;
        const useWaypoints = this.state.useWaypoints;
        const scrollingUp = (data.currentPosition === 'inside') && (data.previousPosition === 'above');

        if ((useWaypoints) && (newSection !== currentSection) && (scrollingUp)) {
            this.setState({
                sectionKey: newSection,
                prevSectionKey: currentSection
            });
        }
    }

    onLeaveWaypoint(newSection, data) {

        const currentSection = this.state.sectionKey;
        const useWaypoints = this.state.useWaypoints;

        if ((useWaypoints) && (newSection !== currentSection)) {

            if (data.waypointTop < WAYPOINT_OFFSET) {
                this.setState({
                    sectionKey: newSection,
                    prevSectionKey: currentSection
                });
            } else {
                this.setState({
                    sectionKey: currentSection
                });
            }
        }

    }

    isPro() {
        const { general } = this.props;

        return general.isPro;
    }

    get isInFiverrProUpdateTest() {
        const { requestContext } = this.props,
            { ab_tests: abTests } = requestContext;

        return isInFiverrProUpdateTest(abTests);
    }

    get shouldRenderProBox() {
        return this.isPro() && !this.isInFiverrProUpdateTest;
    }

    get shouldRenderProUpdateTestComponents() {
        return this.isPro() && this.isInFiverrProUpdateTest;
    }

    isPageOwner() {
        const { requestContext } = this.props,
            { current_user, params } = requestContext;

        return isLoggedInUser(current_user) && (params.username === current_user.username);
    }

    getPageSectionsList() {
        const {
            general,
            overview,
            description,
            faq,
            reviews,
            sellerCard
        } = this.props;

        const { errors } = this.store.getState();

        const { hideRecommended } = this.state;
        const { isStudio } = general;

        return [
            { sectionKey: OVERVIEW, validated: validateOverview({overview, error: errors.overview})},
            { sectionKey: DESCRIPTION, validated: validateDescription({description, error: errors.description})},
            { sectionKey: FAQ, validated: validateFaq({faq, error: errors.faq})},
            { sectionKey: REVIEWS, validated: validateReviews({reviews, error: errors.reviews})},
            { sectionKey: ABOUT_SELLER, validated: !isStudio && validateSellerCard({ sellerCard, error: errors.sellerCard})},
            { sectionKey: PACKAGES_TABLE, validated: this.shouldRenderPackagesTable()},
            { sectionKey: ORDER_DETAILS, validated: this.shouldRenderOrderDetails()},
            { sectionKey: RECOMMENDATIONS, validated: !hideRecommended && this.shouldRenderSimilarGigs()}
        ];
    }

    unwrapChildren() {

        if ((!this.sidebar && !this.main) || (this.displayContents)) {
            return;
        }

        this.orphans = [];
        while (this.sidebar.firstChild) {
            this.orphans.push(this.sidebar.firstChild);
            this.main.insertBefore(this.sidebar.firstChild, this.main.firstChild);
        }

    }

    rewrapChildren() {

        if ((!this.sidebar && !this.main) || (this.displayContents)) {
            return;
        }

        for (let i=0; i<this.orphans.length; i++) {
            this.sidebar.appendChild(this.orphans[i]);
        }

    }

    formatSeo() {
        const { seo } = this.props;

        if (!seo || !seo.schemaMarkup) {
            return;
        }

        const { schemaMarkup } = seo;

        const formattedSeo = {
            '@context': 'http://schema.org/',
            '@type': schemaMarkup.type,
            name: schemaMarkup.gigTitle,
            image: schemaMarkup.gigImage
        };

        const gigRatings = schemaMarkup.gigRatings,
            gigOffers = schemaMarkup.gigOffers;

        if (gigRatings) {
            formattedSeo.aggregateRating = {
                '@type': gigRatings.type,
                ratingValue: gigRatings.value,
                reviewCount: gigRatings.count,
                bestRating: gigRatings.best,
                worstRating: gigRatings.worst
            };
        }

        if (gigOffers) {
            formattedSeo.offers = {
                '@type': gigOffers.type,
                price: gigOffers.price,
                lowPrice: gigOffers.lowPrice,
                highPrice: gigOffers.highPrice,
                priceCurrency: gigOffers.currency,
                availability: "http://schema.org/#{gig_offers['availability']}"
            };
        }

        return sanitizeSeo(formattedSeo);
    }

    onEmptyRecommendedForYouGigs() {
        this.setState({
            hideRecommended: true
        });
    }

    onComparePackagesClick() {
        this.scrollToSection({sectionKey: PACKAGES_TABLE});
    }

    shouldRenderPackagesTable() {
        const { packages, errors } = this.store.getState();

        return validatePackagesTable({ isTriplePackage: isTriplePackage(packages), error: errors.packagesTable });
    }

    shouldRenderMoreServices() {
        const { general } = this.props;
        const { isStudio } = general;

        return !isStudio;
    }

    shouldRenderOutOfOffice() {
        const { general } = this.props;
        const { isOnVacation, isStudio } = general;

        return isOnVacation && !isStudio;
    }

    shouldRenderOrderDetails() {
        const { packages, errors } = this.store.getState();

        return validateOrderDetails({ isSinglePackage: isSinglePackage(packages), error: errors.orderDetails });
    }

    shouldRenderSimilarGigs() {
        const { general } = this.props;
        const { errors } = this.store.getState();
        const { gigStatus, isStudio } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);
        const isPro = this.isPro();

        return !isPro && !errors.recommendedForYou &&
            !isPageOwner && isApproved && !isStudio;
    }

    shouldRenderRecommendedInSc() {
        const { general } = this.props;
        const { gigStatus, isStudio } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);

        return !isPageOwner && isApproved && !isStudio;
    }

    shouldRenderRecentlyGigs() {
        const isPageOwner = this.isPageOwner();
        const isPro = this.isPro();

        return !isPro && !isPageOwner;
    }

    shouldUseStickyLayout() {
        const { general } = this.props;

        return !general.isStudio;
    }

    render() {

        const {
            description,
            faq,
            reviews,
            sellerCard,
            tags,
            topNav,
            gallery,
            overview,
            portfolio,
            customOrder,
            requestContext,
            outOfOffice,
            moreServices,
            otherGigs,
            general,
            studio,
            personalizedContent,
            gigReport
        } = this.props;

        const {
            collections,
            sectionKey,
            isTouch
        } = this.state;

        const { gigStatus, traffiqed, subCategoryId, isStudio, gigId } = general;
        const { params, current_user, ab_tests: abTests } = requestContext;
        const { username, slug } = params;
        const { info, services } = studio;

        const isLoggedIn = isLoggedInUser(current_user);
        const isPageOwner = this.isPageOwner();
        const isPaused = isGigPaused(gigStatus, traffiqed);
        const isPro = this.isPro();

        const shouldRenderPackagesTable = this.shouldRenderPackagesTable();
        const shouldRenderMoreServices = this.shouldRenderMoreServices();
        const shouldRenderOutOfOffice = this.shouldRenderOutOfOffice();

        const showDisclaimer = subCategoryId === SUB_CATEGORIES.BUY_VIDEO_TESTIMONIALS;
        const outerClasses = classNames('gig-page-wrapper responsive', { 'pro-gig': isPro, 'touch': isTouch, 'sidebar-sticky': this.useStickyLayout });
        const sidebarClasses = classNames('sidebar',
            { 'poly-sticky': this.useStickyLayout },
            {'render-pro-update': this.shouldRenderProUpdateTestComponents});

        return (
            <Provider store={this.store}>
                {/* TODO remove the data attributes after refactoring first-message-popup into a  v4 package */}
                {/* The reason to the data attributes is that the first-message-popup in v2 requires this data
                    and the initailData is no longer avaialable in v2 routes */}
                <div className={outerClasses} data-gig-id={gigId} data-username={username} data-slug={slug}>
                    <script type="application/ld+json"
                        dangerouslySetInnerHTML={{__html: this.formatSeo()}}>
                    </script>
                    <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                        {(matches) => {
                            if (matches) {
                                this.rewrapChildren();
                            } else {
                                this.unwrapChildren();
                            }
                            return false;
                        }}
                    </MediaQuery>

                    <TopNav topNav={topNav}
                        pageSectionsList={this.getPageSectionsList()}
                        collections={collections}
                        scrollToSection={this.scrollToSection}
                        isLoggedIn={isLoggedIn}
                        isPageOwner={isPageOwner}
                        isTrustedUser={gigReport.isTrustedUser}
                        selSection={sectionKey}/>

                    <div className="gig-page-top">
                        {isPageOwner && <ShareYourGig/>}
                        {!isPageOwner && isPaused && <PausedGig/>}
                    </div>

                    <div className="gig-page">

                        <aside className={sidebarClasses} ref={(ele) => { this.sidebar = ele; }}>

                            {this.shouldRenderProBox && <ProBox/>}
                            {isStudio && <StudioBox/>}

                            <PackagesTabs customOrder={customOrder}
                                onComparePackagesClick={this.onComparePackagesClick}/>

                            {this.useStickyLayout && <ContactSeller customOrder={customOrder} isPaused={isPaused}/>}
                            {!this.useStickyLayout && <StudioCard studio={studio}/>}

                        </aside>

                        <div className="main" ref={(ele) => { this.main = ele; }}>

                            {this.shouldRenderProUpdateTestComponents && <ProBanner/>}
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(OVERVIEW, data))}
                                onLeave={((data) => this.onLeaveWaypoint(OVERVIEW, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            {shouldRenderOutOfOffice &&
                                <OutOfOffice outOfOffice={outOfOffice} isLoggedIn={isLoggedIn} isPageOwner={isPageOwner}/>
                            }
                            <Overview overview={overview} studioInfo={info} ref={(ele) => { this.sectionOverview = ele; }}
                                scrollToSection={this.scrollToSection} useStickyLayout={this.useStickyLayout}/>

                            <Gallery gallery={gallery} isTouch={isTouch}/>
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(DESCRIPTION, data))}
                                onLeave={((data) => this.onLeaveWaypoint(DESCRIPTION, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            <ReviewsSnippet reviews={reviews} scrollToSection={this.scrollToSection}/>
                            <Description description={description}
                                ref={(ele) => { this.sectionDescription = ele; }}/>

                            {!isStudio && showDisclaimer && <Disclaimer/>}

                            {this.shouldRenderProUpdateTestComponents && <ProCertificateBanner/>}

                            {!isStudio &&
                                <>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(ABOUT_SELLER, data))}
                                        onLeave={((data) => this.onLeaveWaypoint(ABOUT_SELLER, data))}
                                        topOffset={WAYPOINT_OFFSET}/>
                                    <h2 className="section-title about-the-seller" ref={(ele) => { this.sectionAboutSeller = ele; }}>
                                        <I18n k={'gig_page_perseus.general.about_the_seller'}/>
                                    </h2>
                                    <SellerCard {...sellerCard}/>
                                    <Portfolio portfolio={portfolio} isPageOwner={isPageOwner}/>
                                </>
                            }

                            {shouldRenderPackagesTable &&
                                <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(PACKAGES_TABLE, data))}
                                        onLeave={((data) => this.onLeaveWaypoint(PACKAGES_TABLE, data))}
                                        topOffset={WAYPOINT_OFFSET}/>
                                    <PackagesTable services={services} ref={(ele) => {this.sectionPackagesTable = ele;}}/>
                                </MediaQuery>
                            }

                            {this.shouldRenderOrderDetails() &&
                            <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                                <Waypoint onEnter={((data) => this.onEnterWaypoint(ORDER_DETAILS, data))} onLeave={((data) => this.onLeaveWaypoint(ORDER_DETAILS, data))} topOffset={WAYPOINT_OFFSET}/>
                                <OrderDetails ref={(ele) => { this.sectionOrderDetails = ele; }}/>
                            </MediaQuery>
                            }

                            <Waypoint onEnter={((data) => this.onEnterWaypoint(RECOMMENDATIONS, data))} onLeave={((data) => this.onLeaveWaypoint(RECOMMENDATIONS, data))} topOffset={WAYPOINT_OFFSET}/>
                            {this.shouldRenderSimilarGigs() &&
                                <SimilarGigs ref={(ele) => { this.sectionRecommendations = ele; }}
                                    collections={collections}
                                    isLoggedIn={isLoggedIn}
                                    isTouch={isTouch}
                                    updateCollections={this.updateCollections}
                                    onEmptyGigs={this.onEmptyRecommendedForYouGigs}/>
                            }

                            {!isStudio && shouldRenderMoreServices &&
                                <MoreServices {...moreServices} sellerName={params.username}/>
                            }
                            {this.shouldRenderProUpdateTestComponents && <ProVideo/>}
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(FAQ, data))} onLeave={((data) => this.onLeaveWaypoint(FAQ, data))} topOffset={WAYPOINT_OFFSET}/>
                            <Faq faq={faq} ref={(ele) => { this.sectionFaq = ele; }}/>

                            <Waypoint onEnter={((data) => this.onEnterWaypoint(REVIEWS, data))} onLeave={((data) => this.onLeaveWaypoint(REVIEWS, data))} topOffset={WAYPOINT_OFFSET}/>
                            <Reviews reviews={reviews} ref={(ele) => { this.sectionReviews = ele; }}/>

                            <Tags tags={tags}/>
                        </div>
                    </div>

                    <aside className="gig-page-bottom">

                        <OtherGigsBy otherGigs={otherGigs}
                            collections={collections}
                            updateCollections={this.updateCollections}
                            isLoggedIn={isLoggedIn}
                            isTouch={isTouch}
                            studioInfo={info}/>

                        {this.shouldRenderRecommendedInSc() && !isInPersonalizedContentExp(abTests) &&
                        <RecommendedInSc collections={collections}
                            updateCollections={this.updateCollections}
                            isLoggedIn={isLoggedIn}
                            isTouch={isTouch}/>
                        }

                        {this.shouldRenderRecommendedInSc() && isInPersonalizedContentExp(abTests) &&
                        <PersonalizedSectionWrapper personalizedContent={personalizedContent}/>
                        }

                        {this.shouldRenderRecentlyGigs() &&
                        <RecentlyViewedGigs collections={collections}
                            updateCollections={this.updateCollections}
                            isLoggedIn={isLoggedIn}
                            isTouch={isTouch}/>
                        }

                    </aside>

                </div>
            </Provider>
        );
    }
}

GigPage.defaultProps = {
    sellerCard: {},
    studio: {},
    gigReport: {}
};

GigPage.childContextTypes = {
    biEvents: PropTypes.object,
    impressionsFactory: PropTypes.object,
    logger: PropTypes.object,
    general: PropTypes.object,
    requestContext: PropTypes.object,
    currencyConverter: PropTypes.object,
    pathfinderWrapper: PropTypes.object,
    rollouts: PropTypes.object
};

export default GigPage;
